import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Coffee, UtensilsCrossed, Moon, Cookie, Flame, Beef, Wheat, Droplet, ChevronRight, X, Sparkles } from "lucide-react";

const MEAL_TYPES = [
  { key: "desayuno", label: "Desayuno", icon: Coffee },
  { key: "almuerzo", label: "Almuerzo", icon: UtensilsCrossed },
  { key: "cena",     label: "Cena",     icon: Moon },
  { key: "snacks",   label: "Snacks",   icon: Cookie },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const PALMA = [["1 palma", 100], ["1½ palmas", 150], ["2 palmas", 200]];
const PUNO = [["1 puño", 100], ["1½ puños", 150], ["2 puños", 200]];
const LEGUMBRE = [["½ taza cocida", 85], ["1 taza cocida", 170]];
const HOJAS = [["1 taza", 30], ["2 tazas", 60]];
const LECHE = [["½ vaso", 120, "ml"], ["1 vaso", 240, "ml"], ["2 vasos", 480, "ml"]];
const HOUSEHOLD = {
  pollo: PALMA, res: PALMA, cerdo: PALMA, pescado: PALMA, salmon: PALMA,
  arroz_blanco: [["½ taza cocido", 80], ["1 taza cocido", 160], ["1½ tazas cocido", 240]],
  arroz_integral: [["½ taza cocido", 80], ["1 taza cocido", 160], ["1½ tazas cocido", 240]],
  pasta: [["½ taza cocida", 80], ["1 taza cocida", 160], ["1½ tazas cocida", 240]],
  frijoles: LEGUMBRE, lentejas: LEGUMBRE, garbanzos: LEGUMBRE,
  avena: [["¼ taza en seco", 20], ["½ taza en seco", 40], ["1 taza en seco", 80]],
  boniato: PUNO, papa: PUNO, yuca: PUNO, malanga: PUNO, platano_verde: PUNO,
  aceite_oliva: [["1 cucharadita", 5], ["1 cucharada", 15], ["2 cucharadas", 30]],
  leche_entera: LECHE, leche_descremada: LECHE,
  atun: [["½ lata", 75], ["1 lata", 150], ["1 porción", 100]],
  pan_integral: [["1 rebanada", 30], ["2 rebanadas", 60], ["3 rebanadas", 90]],
  yogur_griego: [["3 cucharadas", 45], ["1 envase", 150], ["1 taza", 240]],
  yogur: [["1 vasito", 125], ["2 vasitos", 250]],
  queso_fresco: [["1 lonja", 30], ["2 lonjas", 60], ["3 lonjas", 90]],
  whey: [["½ cacito", 15], ["1 cacito", 30], ["2 cacitos", 60]],
  almendras: [["½ puñado", 15], ["1 puñado pequeño", 30]],
  mani: [["½ puñado", 15], ["1 puñado pequeño", 30]],
  mantequilla_mani: [["1 cucharada", 16], ["2 cucharadas", 32]],
  fresa: [["½ taza", 75], ["1 taza", 150]],
  lechuga: HOJAS, espinaca: HOJAS,
  brocoli: [["½ taza", 45], ["1 taza", 90]],
  zanahoria: [["1 mediana", 60], ["2 medianas", 120]],
  pepino: [["½ pepino", 100], ["1 pepino", 200]],
};

function householdRefs(m) {
  const refs = HOUSEHOLD[m.key];
  if (refs) return refs.map(([label, grams, unit = "g"]) => ({ label, grams, unit }));
  if (m.unit_grams) {
    return [1, 2, 3].map((n) => ({ label: `${n} ${n === 1 ? "unidad" : "unidades"}`, grams: m.unit_grams * n, unit: "g" }));
  }
  return (m.suggestions || [100, 150, 200]).map((g) => ({ label: null, grams: g, unit: "g" }));
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export default function MealLog() {
  const [date, setDate] = useState(todayISO());
  const [day, setDay] = useState(null);
  const [history, setHistory] = useState([]);
  const [adding, setAdding] = useState(null); // meal_type
  const [editing, setEditing] = useState(null); // meal entry
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteDay, setConfirmDeleteDay] = useState(null);
  const [deletingDay, setDeletingDay] = useState(false);
  const topRef = useRef(null);

  const openDay = (d) => {
    setDate(d);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = async () => {
    const [d, h] = await Promise.all([
      api.get(`/meal-log/day?date=${date}`),
      api.get(`/meal-log/history`),
    ]);
    setDay(d.data); setHistory(h.data);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [date]);

  const byMeal = useMemo(() => {
    const map = { desayuno: [], almuerzo: [], cena: [], snacks: [] };
    if (day) for (const m of day.meals) if (map[m.meal_type]) map[m.meal_type].push(m);
    return map;
  }, [day]);

  const toggleKeepLosing = async (value) => {
    try {
      await api.patch("/meal-log/keep-losing", { keep_losing: value });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/meal-log/${confirmDelete.id}`);
      toast.success("Comida eliminada");
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const removeDay = async () => {
    if (deletingDay) return;
    setDeletingDay(true);
    try {
      await api.delete(`/meal-log/day/${confirmDeleteDay}`);
      toast.success("Día eliminado");
      setConfirmDeleteDay(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setDeletingDay(false); }
  };

  if (!day) return <AppShell title="Registrar mi comida"><div className="text-[var(--mep-text-muted)]">Cargando...</div></AppShell>;

  const totals = day.totals;
  const targets = day.recommendation.targets;

  return (
    <AppShell title="Registrar mi comida" subtitle="Anota lo que comes y controla tus macros del día.">
      <div className="space-y-6 scroll-mt-24" data-testid="meal-log-page" ref={topRef}>
        {date !== todayISO() && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-[var(--mep-gold)]/50 bg-[rgba(212,175,55,0.08)]" data-testid="viewing-day-banner">
            <div className="text-sm">
              <span className="text-[var(--mep-text-muted)]">Viendo: </span>
              <span className="font-medium text-[var(--mep-gold)]" data-testid="viewing-day-label">{fmtDate(date)}</span>
            </div>
            <button
              type="button"
              onClick={() => setDate(todayISO())}
              data-testid="back-to-today-btn"
              className="px-3 py-1.5 rounded-full bg-[var(--mep-pink)] text-white text-xs hover:brightness-110"
            >
              Volver a hoy
            </button>
          </div>
        )}

        {/* Date switcher */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-eyebrow">Día</label>
          <input
            type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="meal-date-input"
            className="bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => setDate(todayISO())}
            data-testid="meal-today-btn"
            className="text-xs text-[var(--mep-pink)] hover:underline"
          >
            Hoy
          </button>
        </div>

        {/* Daily goal */}
        <div className="mep-card p-5 border-l-2 border-[var(--mep-gold)]" data-testid="daily-goal">
          <div className="text-eyebrow text-[var(--mep-gold)]">Mi meta diaria</div>
          <div className="font-display text-2xl md:text-3xl mt-2 leading-snug" data-testid="daily-goal-values">
            <span className="text-[var(--mep-pink)]" data-testid="goal-kcal">{Math.round(targets.kcal)} kcal</span>
            <span className="text-[var(--mep-text-muted)]"> · </span>
            <span className="text-[var(--mep-gold)]" data-testid="goal-p">{Math.round(targets.p)} g proteína</span>
            <span className="text-[var(--mep-text-muted)]"> · </span>
            <span data-testid="goal-c">{Math.round(targets.c)} g carbs</span>
            <span className="text-[var(--mep-text-muted)]"> · </span>
            <span data-testid="goal-f">{Math.round(targets.f)} g grasas</span>
          </div>
          {day.recommendation.goal_note && (
            <p className="text-sm text-[var(--mep-text-muted)] mt-3" data-testid="goal-note">{day.recommendation.goal_note}</p>
          )}
          {day.recommendation.can_keep_losing && (
            <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="keep-losing-toggle">
              <input
                type="checkbox"
                checked={day.recommendation.keep_losing}
                onChange={(e) => toggleKeepLosing(e.target.checked)}
                data-testid="keep-losing-checkbox"
                className="accent-[var(--mep-pink)] w-4 h-4"
              />
              Quiero seguir bajando de peso
            </label>
          )}
          {day.recommendation.can_keep_losing && day.recommendation.keep_losing && (
            <TargetWeightForm rec={day.recommendation} onSaved={load} />
          )}
        </div>

        {/* Totals card */}
        <div className="mep-card p-5" data-testid="daily-totals">
          <div>
            <div className="text-eyebrow text-[var(--mep-gold)]">Total del día</div>
            <div className="font-display text-2xl mt-1">{fmtDate(day.date)}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <MacroProgress icon={Flame} label="Calorías" id="kcal" value={totals.kcal} target={targets.kcal} unit="kcal" tone="pink" />
            <MacroProgress icon={Beef} label="Proteína" id="p" value={totals.p} target={targets.p} unit="g" tone="gold" />
            <MacroProgress icon={Wheat} label="Carbohidratos" id="c" value={totals.c} target={targets.c} unit="g" />
            <MacroProgress icon={Droplet} label="Grasas" id="f" value={totals.f} target={targets.f} unit="g" />
          </div>

          <div className="mt-4 p-3 rounded-lg bg-[rgba(232,61,132,0.06)] border-l-2 border-[var(--mep-pink)]" data-testid="daily-recommendation">
            <div className="flex items-center gap-2 text-eyebrow text-[var(--mep-pink)]"><Sparkles size={13} /> Recomendación</div>
            <ul className="text-sm mt-2 space-y-1">
              {day.recommendation.messages.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        </div>

        {/* 4 meal blocks */}
        <div className="grid md:grid-cols-2 gap-4">
          {MEAL_TYPES.map((mt) => {
            const Icon = mt.icon;
            const entries = byMeal[mt.key];
            return (
              <div key={mt.key} className="mep-card p-5" data-testid={`block-${mt.key}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[rgba(212,175,55,0.10)] border border-[var(--mep-gold)]/40 flex items-center justify-center text-[var(--mep-gold)]">
                      <Icon size={16} />
                    </div>
                    <h3 className="font-display text-xl">{mt.label}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAdding(mt.key)}
                    data-testid={`add-${mt.key}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--mep-pink)] text-white text-xs hover:brightness-110"
                  >
                    <Plus size={13} /> Añadir
                  </button>
                </div>

                {entries.length === 0 ? (
                  <div className="text-sm text-[var(--mep-text-muted)] italic">Sin registros aún.</div>
                ) : (
                  <div className="space-y-2">
                    {entries.map((e) => (
                      <div key={e.id} data-testid={`entry-${e.id}`} className="p-3 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{e.text}</div>
                            <div className="text-xs text-[var(--mep-text-muted)] mt-1">
                              {Math.round(e.kcal)} kcal · Proteína {Math.round(e.protein_g)} g · Carbohidratos {Math.round(e.carbs_g)} g · Grasas {Math.round(e.fats_g)} g
                            </div>
                            {e.note && <div className="text-[11px] text-[var(--mep-text-muted)] italic mt-1">{e.note}</div>}
                          </div>
                          <div className="flex gap-1">
                            {!e.items_json?.includes('"preset"') && (
                            <button onClick={() => setEditing(e)} data-testid={`edit-${e.id}`} className="text-[var(--mep-text-muted)] hover:text-[var(--mep-gold)] p-1">
                              <Pencil size={14} />
                            </button>
                            )}
                            <button onClick={() => setConfirmDelete(e)} data-testid={`del-${e.id}`} className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] p-1">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="mep-card p-5" data-testid="meal-history">
            <h3 className="font-display text-xl mb-3">Historial</h3>
            <div className="space-y-2">
              {history.map((d) => (
                <div key={d.date} className="flex items-stretch gap-2">
                <button
                  onClick={() => openDay(d.date)}
                  data-testid={`history-${d.date}`}
                  className={`flex-1 min-w-0 flex items-center gap-3 p-3 rounded-lg border transition ${
                    d.date === date
                      ? "border-[var(--mep-pink)] bg-[rgba(232,61,132,0.06)]"
                      : "border-[var(--mep-border)] hover:border-[var(--mep-pink)]/40 bg-[var(--mep-elev)]"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm">{fmtDate(d.date)}</div>
                    <div className="text-xs text-[var(--mep-text-muted)]">
                      {Math.round(d.kcal)} kcal · Proteína {Math.round(d.p)} g · Carbohidratos {Math.round(d.c)} g · Grasas {Math.round(d.f)} g · {d.count} comidas
                    </div>
                    <HistorySummary day={d} targets={targets} />
                  </div>
                  <ChevronRight size={16} className="text-[var(--mep-text-muted)]" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteDay(d.date)}
                  data-testid={`history-delete-${d.date}`}
                  aria-label="Eliminar día"
                  title="Eliminar día"
                  className="shrink-0 px-2 rounded-lg border border-[var(--mep-border)] text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] hover:border-[var(--mep-pink)]/40 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {adding && (
        <MealModal
          mode="add"
          mealType={adding}
          date={date}
          onClose={() => setAdding(null)}
          onSaved={async () => { setAdding(null); await load(); }}
        />
      )}
      {editing && (
        <MealModal
          mode="edit"
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[var(--mep-surface)] border border-[var(--mep-border)] max-w-sm w-full rounded-2xl p-6">
            <div className="font-display text-lg mb-2">¿Eliminar esta comida?</div>
            <p className="text-sm text-[var(--mep-text-muted)]">{confirmDelete.text}</p>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border border-[var(--mep-border)] text-sm">Cancelar</button>
              <button onClick={remove} data-testid="confirm-del" className="px-4 py-2 rounded-lg bg-[var(--mep-pink)] text-white font-medium text-sm">Eliminar</button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteDay && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDeleteDay(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[var(--mep-surface)] border border-[var(--mep-border)] max-w-sm w-full rounded-2xl p-6" data-testid="delete-day-dialog">
            <div className="font-display text-lg mb-2">Eliminar día · {fmtDate(confirmDeleteDay)}</div>
            <p className="text-sm text-[var(--mep-text-muted)]">¿Eliminar todos los registros de comida de esta fecha? Esta acción no se puede deshacer.</p>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setConfirmDeleteDay(null)} data-testid="delete-day-cancel" className="px-4 py-2 rounded-lg border border-[var(--mep-border)] text-sm">Cancelar</button>
              <button onClick={removeDay} disabled={deletingDay} data-testid="delete-day-confirm" className="px-4 py-2 rounded-lg bg-[var(--mep-pink)] text-white font-medium text-sm disabled:opacity-60">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const HISTORY_MACROS = [
  { key: "kcal", label: "Calorías", unit: "kcal" },
  { key: "p", label: "Proteína", unit: "g" },
  { key: "c", label: "Carbohidratos", unit: "g" },
  { key: "f", label: "Grasas", unit: "g" },
];

const HISTORY_TIPS = {
  kcal_low: "Hoy quedaste por debajo de tu meta calórica. Puedes aumentar un poco las porciones con alimentos nutritivos.",
  kcal_high: "Hoy superaste tu meta calórica. Intenta que la próxima comida sea más ligera y prioriza proteína y vegetales.",
  p_low: "Te faltó proteína. Puedes añadir huevos, pollo, pescado, yogur u otra opción alta en proteína.",
  p_high: "Hoy la proteína estuvo por encima de tu meta. Puedes equilibrar con más vegetales y carbohidratos en la próxima comida.",
  c_low: "Te faltaron carbohidratos. Puedes añadir arroz, boniato, yuca, avena, frutas u otra fuente de carbohidratos.",
  c_high: "Hoy los carbohidratos estuvieron altos. Intenta reducir un poco arroz, pan, dulces u otras porciones de carbohidratos en la próxima comida.",
  f_low: "Te faltaron grasas saludables. Puedes añadir aguacate, frutos secos o un poco de aceite de oliva.",
  f_high: "Hoy las grasas estuvieron por encima de tu meta. Intenta reducir fritos, quesos grasos, embutidos o exceso de aceite.",
  ok: "Vas bien encaminado y estuviste cerca de tus metas del día.",
};

const TargetWeightForm = ({ rec, onSaved }) => {
  const [value, setValue] = useState(rec.target_weight_kg ?? "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(rec.target_weight_kg == null);
  const min = rec.range_min_kg;
  const max = rec.range_max_kg;
  const save = async () => {
    const v = parseFloat(value);
    if (!Number.isFinite(v)) { setError("Escribe tu meta de peso en kg."); return; }
    if (v < min) { setError("Esa meta está por debajo de tu rango fitness orientativo. Elige un peso dentro del rango recomendado."); return; }
    if (v > max) { setError("Esa meta está por encima de tu rango fitness orientativo. Elige un peso dentro del rango recomendado."); return; }
    setError(""); setSaving(true);
    try {
      await api.patch("/meal-log/target-weight", { target_weight_kg: v });
      toast.success("Meta de peso guardada");
      await onSaved();
      setEditing(false);
    } catch (e) { setError(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 p-4 rounded-lg border border-[var(--mep-border)] bg-[var(--mep-surface-2)]" data-testid="target-weight-form">
      <div className="text-sm font-medium">¿Hasta qué peso quieres llegar?</div>
      {!editing && rec.target_weight_kg != null ? (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3" data-testid="target-weight-readonly">
          <div className="text-sm space-y-0.5">
            <div>Meta de peso: <span className="font-medium text-[var(--mep-gold)]" data-testid="target-weight-value">{rec.target_weight_kg} kg</span></div>
            <div className="text-[var(--mep-text-muted)]">Peso actual: <span data-testid="current-weight-value">{rec.current_weight_kg} kg</span></div>
          </div>
          <button
            type="button" onClick={() => { setValue(rec.target_weight_kg); setError(""); setEditing(true); }}
            data-testid="target-weight-edit"
            className="px-3 py-1.5 rounded-full border border-[var(--mep-gold)]/60 text-[var(--mep-gold)] text-xs hover:bg-[rgba(212,175,55,0.08)] transition-colors"
          >
            Editar meta
          </button>
        </div>
      ) : (
      <>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <label className="text-sm text-[var(--mep-text-muted)]" htmlFor="target-weight-input">Meta de peso:</label>
        <input
          id="target-weight-input" type="number" step="0.1" min={min} max={max}
          value={value} onChange={(e) => setValue(e.target.value)}
          data-testid="target-weight-input"
          className="w-24 bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-[var(--mep-pink)]"
        />
        <span className="text-sm">kg</span>
        <button
          type="button" onClick={save} disabled={saving}
          data-testid="target-weight-save"
          className="px-3 py-1.5 rounded-full bg-[var(--mep-pink)] text-white text-xs hover:brightness-110 disabled:opacity-60"
        >
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
      <p className="text-xs text-[var(--mep-text-muted)] mt-2" data-testid="target-weight-range">
        Tu rango fitness orientativo es {min}–{max} kg. Elige una meta dentro de este rango.
      </p>
      {error && <p className="text-xs text-[var(--mep-pink)] mt-1" data-testid="target-weight-error">{error}</p>}
      </>
      )}
    </div>
  );
};

const HistorySummary = ({ day, targets }) => {
  const rows = HISTORY_MACROS.map((m) => {
    const v = Math.round(day[m.key]);
    const t = Math.round(targets[m.key]);
    const diff = v - t;
    const near = v >= t && v <= t * 1.1;
    const status = near ? "Meta alcanzada"
      : diff > 0 ? `${diff} ${m.unit} por encima`
      : v >= t * 0.9 ? `Cerca de la meta · faltan ${-diff} ${m.unit}`
      : `Faltan ${-diff} ${m.unit}`;
    return { ...m, v, t, diff, near, status };
  });
  const pct = Object.fromEntries(rows.map((r) => [r.key, r.t ? r.v / r.t : 1]));
  const pGap = rows.find((r) => r.key === "p").t - rows.find((r) => r.key === "p").v;
  const cand = [];
  if (pct.p < 0.9) cand.push(HISTORY_TIPS.p_low);
  else if (pct.p < 1) cand.push(`Te faltan solo ${pGap} g de proteína para completar tu meta. Puedes añadir una pequeña porción de huevo, pollo, pescado, yogur u otra opción alta en proteína.`);
  if (pct.kcal > 1.2) cand.push(HISTORY_TIPS.kcal_high);
  else if (pct.kcal < 0.8 && !(pct.c < 0.8)) cand.push(HISTORY_TIPS.kcal_low);
  if (pct.f > 1.1) cand.push(HISTORY_TIPS.f_high);
  if (pct.c > 1.2) cand.push(HISTORY_TIPS.c_high);
  else if (pct.c < 0.8) cand.push(HISTORY_TIPS.c_low);
  const tips = cand.length ? cand.slice(0, 2) : [HISTORY_TIPS.ok];
  return (
    <div className="mt-2 space-y-0.5" data-testid={`history-summary-${day.date}`}>
      {rows.map((r) => (
        <div key={r.key} className="text-xs" data-testid={`history-${day.date}-${r.key}`}>
          <span className="text-[var(--mep-text-muted)]">{r.label}: </span>
          <span>{r.v} / {r.t} {r.unit}</span>
          <span className={r.near ? "text-[var(--mep-gold)]" : "text-[var(--mep-text-muted)]"}> — {r.status}</span>
        </div>
      ))}
      {tips.map((tip, i) => (
        <div key={i} className="text-xs italic text-[var(--mep-text)] mt-1.5 pl-2 border-l-2 border-[var(--mep-pink)]" data-testid={`history-tip-${day.date}-${i}`}>
          {tip}
        </div>
      ))}
    </div>
  );
};

const MacroProgress = ({ icon: Icon, label, id, value, target, unit, tone }) => {
  const v = Math.round(value);
  const t = Math.round(target);
  const pct = t ? Math.round((v / t) * 100) : 0;
  const color = tone === "pink" ? "text-[var(--mep-pink)]" : tone === "gold" ? "text-[var(--mep-gold)]" : "text-[var(--mep-text)]";
  const bar = tone === "pink" ? "bg-[var(--mep-pink)]" : tone === "gold" ? "bg-[var(--mep-gold)]" : "bg-[var(--mep-text-muted)]";
  return (
    <div data-testid={`progress-${id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[var(--mep-text-muted)]">
          <Icon size={12} /> {label}
        </div>
        <div className="text-[11px] text-[var(--mep-text-muted)]" data-testid={`progress-${id}-pct`}>{pct}%</div>
      </div>
      <div className="mt-1 text-sm" data-testid={`progress-${id}-value`}>
        <span className={`font-display text-xl ${color}`}>{v}</span>
        <span className="text-[var(--mep-text-muted)]"> / {t} {unit}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-[var(--mep-elev)] overflow-hidden">
        <div className={`h-full ${bar} transition-[width] duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
};

function MealModal({ mode, mealType, date, entry, onClose, onSaved }) {
  const [text, setText] = useState(entry?.text || "");
  const [note, setNote] = useState(entry?.note || "");
  const [quantities, setQuantities] = useState({});
  const [customQty, setCustomQty] = useState({});
  const [missing, setMissing] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [saving, setSaving] = useState(false);
  const effectiveType = mode === "add" ? mealType : entry?.meal_type;

  const submit = async (ev) => {
    ev.preventDefault();
    if (!text.trim()) { toast.error("Escribe lo que comiste."); return; }
    setSaving(true);
    try {
      const exact = Object.fromEntries(Object.entries(customQty).filter(([, v]) => v > 0));
      const payload = { text, quantities: { ...quantities, ...exact }, note: note || undefined };
      if (mode === "add") {
        payload.meal_type = effectiveType;
        payload.date = date;
        const r = await api.post("/meal-log", payload);
        if (r.status === 422 || r.data?.missing?.length) {
          setMissing(r.data.missing || []);
          setUnknown(r.data.unknown || []);
          toast.message("Falta la cantidad de algunos alimentos.");
        } else {
          toast.success("Comida registrada");
          await onSaved();
        }
      } else {
        const r = await api.patch(`/meal-log/${entry.id}`, payload);
        if (r.status === 422 || r.data?.missing?.length) {
          setMissing(r.data.missing || []);
          setUnknown(r.data.unknown || []);
          toast.message("Falta la cantidad de algunos alimentos.");
        } else {
          toast.success("Actualizado");
          await onSaved();
        }
      }
    } catch (err) {
      // Axios throws on 422; surface its detail.
      const data = err?.response?.data;
      if (data?.missing) {
        setMissing(data.missing);
        setUnknown(data.unknown || []);
        toast.message("Falta la cantidad de algunos alimentos.");
      } else {
        toast.error(data?.detail || "No se pudo guardar");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="meal-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--mep-surface)] border border-[var(--mep-border)] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-eyebrow text-[var(--mep-gold)]">{mode === "add" ? "Añadir comida" : "Editar comida"}</div>
            <h3 className="font-display text-xl mt-1 capitalize">{effectiveType}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--mep-text-muted)] hover:text-white p-1"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">¿Qué comiste?</label>
            <textarea
              data-testid="meal-text-input"
              value={text}
              onChange={(e) => { setText(e.target.value); setMissing([]); setQuantities({}); setCustomQty({}); }}
              rows={3}
              placeholder="Ej. 2 huevos revueltos y 150 g de boniato"
              className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-pink)]"
            />
          </div>

          {missing.length > 0 && (
            <div className="p-3 rounded-lg bg-[rgba(232,61,132,0.08)] border border-[var(--mep-pink)]/40 space-y-3" data-testid="missing-quantities">
              <div className="text-eyebrow text-[var(--mep-pink)]">Falta la cantidad</div>
              {missing.map((m) => {
                const refs = householdRefs(m);
                const exact = customQty[m.key] > 0 ? customQty[m.key] : null;
                const chosen = !exact ? refs.find((r) => r.grams === quantities[m.key]) : null;
                return (
                <div key={m.key}>
                  <div className="text-sm font-medium capitalize mb-1">{m.name}</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {refs.map((r) => (
                      <button
                        key={r.grams} type="button"
                        onClick={() => setQuantities((q) => ({ ...q, [m.key]: r.grams }))}
                        data-testid={`qty-${m.key}-${r.grams}`}
                        className={`px-3 py-1.5 rounded-full border text-xs transition ${chosen && chosen.grams === r.grams ? "bg-[var(--mep-gold)] text-black border-[var(--mep-gold)]" : "border-[var(--mep-border)] text-[var(--mep-text)]"}`}
                      >
                        {r.label ? `${r.label} ≈ ${r.grams} ${r.unit}` : `${r.grams} g`}
                      </button>
                    ))}
                    <input
                      type="number" min="1" max="2000" placeholder="Otra cantidad (g o ml)"
                      data-testid={`qty-custom-${m.key}`}
                      value={customQty[m.key] || ""}
                      onChange={(e) => setCustomQty((q) => ({ ...q, [m.key]: parseFloat(e.target.value) || 0 }))}
                      className="w-40 bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-2 py-1.5 text-xs"
                    />
                  </div>
                  {(exact || chosen) && (
                    <div className="text-[11px] text-[var(--mep-gold)] mt-1" data-testid={`qty-selected-${m.key}`}>
                      {exact ? `Usando ${exact} g/ml exactos.` : `Estimación: ${chosen.label ? `${chosen.label} ≈ ` : ""}${chosen.grams} ${chosen.unit}.`}
                    </div>
                  )}
                </div>
                );
              })}
              <div className="text-[11px] text-[var(--mep-text-muted)] italic" data-testid="qty-estimate-note">Son estimaciones. Si conoces el peso exacto, escríbelo.</div>
              <div className="text-[11px] text-[var(--mep-text-muted)] italic">Indica cuánto comiste de cada uno. Pulsa guardar para recalcular.</div>
            </div>
          )}

          {unknown.length > 0 && (
            <div className="text-[11px] text-[var(--mep-text-muted)] italic">
              No reconocimos: {unknown.join(", ")}. Estos alimentos no se contarán en los macros.
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Nota (opcional)</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-pink)]"
            />
          </div>

          <button
            type="submit" disabled={saving}
            data-testid="meal-submit"
            className="w-full py-3 rounded-lg bg-[var(--mep-pink)] text-white font-semibold hover:brightness-110 transition disabled:opacity-50"
          >
            {saving ? "Guardando..." : missing.length > 0 ? "Recalcular con cantidades" : (mode === "add" ? "Guardar comida" : "Guardar cambios")}
          </button>
        </form>
      </div>
    </div>
  );
}
